import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";
import {
  ShieldCheck, ScrollText, CheckCircle2, Loader2, MapPin, IdCard, Phone, Home,
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Card } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { useAuth, API, formatApiErrorDetail } from "../context/AuthContext";

/**
 * Private legal document page (route is protected by ProtectedRoute with matching role).
 * /legal/parents → role="parent"   |   /legal/educators → role="educator"
 */
export default function LegalDoc({ role }) {
  const { t, i18n } = useTranslation();
  const locale = (i18n.resolvedLanguage || i18n.language || "fr").startsWith("en") ? "en-US" : "fr-FR";
  const { user, refreshMe } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  const [form, setForm] = useState({
    typed_name: "",
    confirmation: "",
    address: "",
    id_number: "",
    city: "",
    phone: "",
  });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const path = role === "parent" ? "parent" : "educator";
      const res = await axios.get(`${API}/legal/${path}`);
      setData(res.data);
      setForm((f) => ({
        ...f,
        typed_name: user?.name || "",
        city: user?.profile?.city || "",
        address: user?.profile?.address || user?.profile?.city || "",
        phone: user?.profile?.phone || "",
        id_number: user?.profile?.id_number || "",
      }));
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
      navigate("/dashboard");
    } finally {
      setLoading(false);
    }
  }, [role, user, navigate]);

  useEffect(() => { load(); }, [load]);

  const onChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const onAccept = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await axios.post(`${API}/legal/accept`, {
        role,
        typed_name: form.typed_name.trim(),
        confirmation: form.confirmation.trim(),
        address: role === "educator" ? form.address.trim() : undefined,
        id_number: role === "educator" ? form.id_number.trim() : undefined,
        city: form.city.trim(),
      });
      toast.success(t("legal.signedToast"));
      await refreshMe();
      await load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setSubmitting(false);
    }
  };

  if (loading || !data) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center">
        <Loader2 className="h-6 w-6 animate-spin text-[#16A34A]" />
      </div>
    );
  }

  const accepted = !!data.accepted_at;
  const expected = t("legal.expectedPhrase").toLowerCase();

  return (
    <div className="min-h-screen bg-[#FAFCFB]" data-testid={`legal-${role}-page`}>
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="rounded-3xl bg-white border border-[#E5EBE7] p-8 sm:p-10 relative overflow-hidden">
          <span className="geo-shape bg-[#16A34A]/12 h-64 w-64 -top-20 -right-20" aria-hidden />
          <div className="relative">
            <span className="pill"><ScrollText className="h-3.5 w-3.5" /> {t("legal.pill")}</span>
            <h1 className="mt-4 font-display text-4xl sm:text-5xl font-bold text-[#0A2518]" data-testid="legal-title">{data.title}</h1>
            <p className="mt-3 text-[#4B5563] max-w-3xl">
              {role === "parent" ? t("legal.introParent") : t("legal.introEducator")}
            </p>
            {accepted && (
              <div className="mt-4 inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-[#F0FDF4] text-[#15803D] text-sm font-medium border border-[#16A34A]/30" data-testid="legal-accepted-badge">
                <CheckCircle2 className="h-4 w-4" /> {t("legal.signedOn", { date: new Date(data.accepted_at).toLocaleDateString(locale) })}
              </div>
            )}
          </div>
        </div>

        {/* Document */}
        <Card className="mt-8 p-6 sm:p-8 border-[#E5EBE7]" data-testid="legal-document">
          <pre className="whitespace-pre-wrap font-sans text-sm sm:text-[15px] leading-relaxed text-[#0A2518]">
            {data.document}
          </pre>
        </Card>

        {/* Acceptance */}
        {accepted ? (
          <Card className="mt-8 p-6 border-[#E5EBE7] bg-[#F0FDF4]/40 text-center">
            <CheckCircle2 className="h-12 w-12 text-[#16A34A] mx-auto mb-3" />
            <h3 className="font-display text-xl font-semibold text-[#0A2518]">{t("legal.signedTitle")}</h3>
            <p className="mt-1 text-sm text-[#4B5563]">
              {t("legal.signedDesc", { datetime: new Date(data.accepted_at).toLocaleString(locale), name: data.typed_name })}
            </p>
            <Button onClick={() => navigate("/dashboard")} className="mt-5 rounded-full bg-[#16A34A] hover:bg-[#15803D] text-white">
              {t("legal.goToSpace")}
            </Button>
          </Card>
        ) : (
          <Card className="mt-8 p-7 border-[#16A34A]/40 bg-white shadow-lg" data-testid="legal-sign-form">
            <h3 className="font-display text-2xl font-semibold text-[#0A2518] flex items-center gap-2 mb-2">
              <ShieldCheck className="h-5 w-5 text-[#16A34A]" /> {t("legal.signTitle")}
            </h3>
            <p className="text-sm text-[#4B5563] mb-5">{t("legal.signIntro")}</p>
            <form onSubmit={onAccept} className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className={role === "educator" ? "md:col-span-2" : "md:col-span-1"}>
                <Label className="text-[#0A2518]">{t("legal.typedName")}</Label>
                <Input name="typed_name" value={form.typed_name} onChange={onChange} required className="mt-1.5 h-11" data-testid="legal-typed-name" />
              </div>

              <div>
                <Label className="text-[#0A2518] flex items-center gap-1.5"><MapPin className="h-3.5 w-3.5" /> {t("legal.city")}</Label>
                <Input name="city" value={form.city} onChange={onChange} required className="mt-1.5 h-11" placeholder={t("legal.cityPh")} data-testid="legal-city" />
              </div>

              {role === "educator" && (
                <>
                  <div>
                    <Label className="text-[#0A2518] flex items-center gap-1.5"><Home className="h-3.5 w-3.5" /> {t("legal.address")}</Label>
                    <Input name="address" value={form.address} onChange={onChange} required className="mt-1.5 h-11" placeholder={t("legal.addressPh")} data-testid="legal-address" />
                  </div>
                  <div>
                    <Label className="text-[#0A2518] flex items-center gap-1.5"><Phone className="h-3.5 w-3.5" /> {t("legal.phone")}</Label>
                    <Input name="phone" value={form.phone} onChange={onChange} className="mt-1.5 h-11" placeholder={t("legal.phonePh")} data-testid="legal-phone" />
                  </div>
                  <div className="md:col-span-2">
                    <Label className="text-[#0A2518] flex items-center gap-1.5"><IdCard className="h-3.5 w-3.5" /> {t("legal.idNumber")}</Label>
                    <Input name="id_number" value={form.id_number} onChange={onChange} required className="mt-1.5 h-11" data-testid="legal-id-number" />
                  </div>
                </>
              )}

              <div className="md:col-span-2">
                <Label className="text-[#0A2518]">{t("legal.confirmation")}</Label>
                <Input
                  name="confirmation"
                  value={form.confirmation}
                  onChange={onChange}
                  required
                  className="mt-1.5 h-11 font-display italic"
                  placeholder={t("legal.confirmationPh")}
                  data-testid="legal-confirmation"
                />
                <p className="mt-1 text-xs text-[#4B5563]">{t("legal.confirmationHint")}</p>
              </div>

              <div className="md:col-span-2">
                <Button
                  type="submit"
                  disabled={submitting || form.confirmation.trim().toLowerCase() !== expected}
                  className="w-full h-12 rounded-full bg-[#16A34A] hover:bg-[#15803D] text-white"
                  data-testid="legal-accept-btn"
                >
                  {submitting ? (<><Loader2 className="h-4 w-4 mr-2 animate-spin" />{t("legal.signing")}</>) : (t("legal.signCta"))}
                </Button>
              </div>
            </form>
          </Card>
        )}
      </div>
    </div>
  );
}
